import { faroCoreApiClient } from "@api/faro-core-api-client";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { BaseGroupIdProps } from "@custom-types/base-types";
import { useAppParams } from "@router/router-helper";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { useEffect, useState } from "react";

interface UseMemberGroupDetails {
  /** Details of a group that the member belongs to */
  groupDetails: FaroCoreApiTypes.LegacyGroupDetailResponse | null;

  /** Whether the group details are currently being fetched */
  isFetchingGroupDetails: boolean;
}

/** Fetches the details of group of a company member */
export function useMemberGroupDetails({
  groupId,
}: BaseGroupIdProps): UseMemberGroupDetails {
  const { companyId, memberId, teamId } = useAppParams();
  const { handleErrorWithToast } = useErrorContext();

  const [isFetchingGroupDetails, setIsFetchingGroupDetails] =
    useState<boolean>(false);
  const [groupDetails, setGroupDetails] =
    useState<FaroCoreApiTypes.LegacyGroupDetailResponse | null>(null);

  useEffect(() => {
    async function fetchMemberGroupDetails(): Promise<void> {
      // Fetch group details only if the member or team id is available
      const id = memberId || teamId;
      if (companyId && id) {
        setIsFetchingGroupDetails(true);

        try {
          const data =
            await faroCoreApiClient.groups.getCompanyMemberGroupDetails({
              companyId,
              groupId,
              userIdentity: id,
            });

          setGroupDetails(data);
        } catch (error) {
          handleErrorWithToast({
            id: `getCompanyMemberGroupDetails-${Date.now().toString()}`,
            title: "Cannot fetch workspace member group details",
            error,
          });
        }

        setIsFetchingGroupDetails(false);
      }
    }
    void fetchMemberGroupDetails();
  }, [companyId, groupId, handleErrorWithToast, memberId, teamId]);

  return { groupDetails, isFetchingGroupDetails };
}
